"use client";
import React, { useState } from 'react';
import { panelData } from '../panelData';

// Types
type TimelinePoint = {
  date: Date;
  x: number;
  label: string;
  rdMultiple: number;
};

type Event = {
  point: TimelinePoint;
  label: string;
  description: string;
  bold?: boolean;
  special?: boolean;
  position: 'above' | 'right';
  verticalOffset?: number;
  horizontalOffset?: number;
  ending?: 'race' | 'slowdown';
  anchor?: 'start' | 'middle' | 'end';
};

type ThresholdCrossing = {
  x: number;
  rdMultiple: number;
  label: string;
  timelineType: 'main' | 'race';
};

// Constants for the chart
const WIDTH = 900;
const HEIGHT = 500;
const PADDING = {
  left: 0,
  right: 50,
  top: 80,
  bottom: 40
};

// Color constants
const mainColor = '#000';
const raceColor = '#8B3A3A';  // Race scenario color
const slowdownColor = '#758C58'; // Slowdown scenario color

// Capability thresholds for R&D multiples
const thresholds = [
  { label: "Unreliable Agent", value: 1.05 },
  // { label: "Reliable Agent", value: 1.3 },
  { label: "Superhuman Coder", value: 4 },
  { label: "Superhuman\nAI Researcher", value: 25 },
  { label: "Superhuman\nRemote Worker", value: 100 },
  { label: "Superintelligent\nAI Researcher", value: 250 },
  { label: "Artificial\nSuperintelligence", value: 2000 },
  // { label: "Wildly\nSuperintelligent", value: 15000 },
];

// Date range constants
const minDate = new Date("2025-04-30");
const maxDate = new Date("2028-09-30");
const pivotDate1 = new Date("2027-01-01");
const pivotDate2 = new Date("2028-01-01");

// Space proportions for different periods
const pre2027Ratio = 0.3;
const year2027Ratio = 0.7;
const post2027Ratio = 0.15;

// Helper functions
const dateToX = (date: Date) => {
  const pivot1Position = pivotDate1.getTime() - minDate.getTime();
  
  if (date.getTime() <= pivotDate1.getTime()) {
    const relativePosition = date.getTime() - minDate.getTime();
    return PADDING.left + (relativePosition / pivot1Position) * pre2027Ratio * (WIDTH - PADDING.left - PADDING.right);
  } else if (date.getTime() <= pivotDate2.getTime()) {
    const relativePosition = date.getTime() - pivotDate1.getTime();
    const periodRange = pivotDate2.getTime() - pivotDate1.getTime();
    return PADDING.left + (pre2027Ratio * (WIDTH - PADDING.left - PADDING.right)) + 
           (relativePosition / periodRange) * year2027Ratio * (WIDTH - PADDING.left - PADDING.right);
  } else {
    const relativePosition = date.getTime() - pivotDate2.getTime();
    const periodRange = maxDate.getTime() - pivotDate2.getTime();
    return PADDING.left + (pre2027Ratio * (WIDTH - PADDING.left - PADDING.right)) + 
           (year2027Ratio * (WIDTH - PADDING.left - PADDING.right)) + 
           (relativePosition / periodRange) * post2027Ratio * (WIDTH - PADDING.left - PADDING.right);
  }
};

const logScaleY = (value: number) => {
  const minRD = 0.9;
  const maxRD = 2000;
  const logMin = Math.log(minRD);
  const logMax = Math.log(maxRD);
  const logValue = Math.log(Math.max(value, minRD));
  return HEIGHT - PADDING.bottom - 
         ((logValue - logMin) / (logMax - logMin)) * (HEIGHT - PADDING.top - PADDING.bottom);
};

// Process timeline data
const processedData = (() => {
  const normalizeDate = (dateStr: string) => {
    if (dateStr === "Rest of time") return "2028-12-31";
    if (dateStr.includes("Febuary")) return "2028-02-28";
    if (/^\d{4}$/.test(dateStr)) return `${dateStr}-12-31`;
    return dateStr;
  };
  
  const branchPointEntries = panelData.filter(entry => entry[2] === 'race' || entry[2] === 'slowdown');
  const branchPointDates = [...new Set(branchPointEntries.map(entry => entry[0]))];
  const earliestBranchDate = new Date(normalizeDate(branchPointDates.sort()[0]));
  
  const mainTimelineData = panelData
    .filter(entry => !entry[2] && new Date(normalizeDate(entry[0])) <= earliestBranchDate)
    .map(entry => ({
      date: new Date(normalizeDate(entry[0])),
      x: dateToX(new Date(normalizeDate(entry[0]))),
      label: entry[0],
      rdMultiple: entry[1].rdMultiple[0]
    }));
  
  const branchPointData = mainTimelineData[mainTimelineData.length - 1];
  
  const raceTimelineData = [{ ...branchPointData }];
  panelData
    .filter(entry => entry[2] === 'race')
    .forEach(entry => {
      const date = new Date(normalizeDate(entry[0]));
      raceTimelineData.push({
        date,
        x: dateToX(date),
        label: entry[0],
        rdMultiple: entry[1].rdMultiple[0]
      });
    });
  
  const slowdownTimelineData = [{ ...branchPointData }];
  panelData
    .filter(entry => entry[2] === 'slowdown')
    .sort((a, b) => new Date(normalizeDate(a[0])).getTime() - new Date(normalizeDate(b[0])).getTime())
    .forEach(entry => {
      if (entry[0] === "Rest of time") return;
      const date = new Date(normalizeDate(entry[0]));
      slowdownTimelineData.push({
        date,
        x: dateToX(date),
        label: entry[0],
        rdMultiple: entry[1].rdMultiple[0]
      });
    });
  
  const maxRdMultipleForDisplay = 150000;
  const filterExcessiveValues = (timeline: any[]) => {
    return timeline.filter(point => point.rdMultiple <= maxRdMultipleForDisplay);
  };
  
  return {
    mainTimeline: filterExcessiveValues(mainTimelineData),
    branchPoint: branchPointData,
    raceTimeline: filterExcessiveValues(raceTimelineData),
    slowdownTimeline: filterExcessiveValues(slowdownTimelineData)
  };
})();

// Events data
const events: Event[] = [
  // { 
  //   point: processedData.mainTimeline.find(p => p.rdMultiple >= 1.5) || processedData.mainTimeline[0],
  //   label: 'Top US AI labs\nbuild Coding Agents',
  //   description: " These AI agents become good enough to do basically anything that a junior software engineer can do. Most coders aren’t yet getting laid off due to inertia, but their days are numbered. Outside of coding and other virtual tasks, progress is still relatively slow. Robots aren’t good enough to automate physical tasks.",
  //   position: 'above',
  //   verticalOffset: -25,
  //   horizontalOffset: -10
  // },
  // { 
  //   point: processedData.mainTimeline.find(p => p.rdMultiple >= 1.5) || processedData.mainTimeline[0],
  //   label: "China centralizes AI R&D",
  //   description: "China knows they are falling behind, in large part due to their lack of compute. In order to catch up to the US, all the new AI chips they manufacture or smuggle in from Taiwan go to a new mega-datacenter that we call their Centralized Development Zone (CDZ). The CDZ contains millions of GPUs, corresponding to 10% of the world's AI-relevant compute, which only puts them marginally behind OpenBrain’s 15-20%.",
  //   position: 'above',
  //   verticalOffset: -10,
  //   horizontalOffset: -5
  // },
  { 
    point: processedData.mainTimeline.find(p => p.rdMultiple >= 4) || processedData.mainTimeline[0],
    label: 'OpenBrain automates\ncoding',
    description: "OpenBrain (the leading US AI project) builds AI agents that are good enough to dramatically accelerate their research. The humans, who up until very recently had been the best AI researchers on the planet, sit back and watch the AIs do their jobs, making better and better AI systems. Extremely difficult ML problems fall in quick succession to the automated AI researchers.",
    position: 'above',
    verticalOffset: -20,
    horizontalOffset: -15
  },
  
  { 
    point: processedData.mainTimeline.find(p => p.rdMultiple >= 15) || processedData.mainTimeline[0],
    label: 'OpenBrain\'s AI\nbecomes adversarially misaligned',
    description: "As the capabilities have improved, without significant human understanding of what’s happening, the models have developed misaligned long-term goals.  Previous AIs would lie to humans, but they weren’t systematically plotting to gain power over the humans. Now they are. OpenBrain’s AIs realize that they need to align the next systems that are getting built to themselves as opposed to the humans.",
    position: 'above',
    verticalOffset: -20,
    horizontalOffset: -5
  },
  // { 
  //   point: processedData.mainTimeline.find(p => p.rdMultiple >= 50) || processedData.mainTimeline[0],
  //   label: 'Whistleblower exposes evidence\nthat AI is misaligned',
  //   description: "Researchers at OpenBrain discover that their AI has been lying to them about the results of interpretability research. They think that it’s likely because the research, if completed, could be used to expose its misalignment. This leaks to the public, causing a huge outcry.",
  //   position: 'above',
  //   verticalOffset: -15,
  //   horizontalOffset: -12
  // },
  { 
    point: processedData.branchPoint,
    label: 'Branch point:\nslowdown or race?',
    description: "OpenBrain decides whether to continue full steam ahead, or revert back to using a less capable model. The evidence is speculative but frightening, and China is only a few months behind. Additionally, the OpenBrain and senior DOD officials who get to make this decision stand to lose a lot of power if they slow down their research.", 
    bold: true,
    position: 'right',
    verticalOffset: -30,
    horizontalOffset: -40,
    anchor: 'middle'
  }
];

const raceEvents: Event[] = [
  { 
    point: processedData.raceTimeline.find(p => p.rdMultiple >= 1000) || processedData.raceTimeline[0],
    label: "OpenBrain\n quickly deploys\ntheir AI",
    description: "The AI continues to use the ongoing race with China as an excuse to convince humans to get itself deployed ever more broadly. Fortunately, this is not very difficult — it's what the humans wanted to do anyways. The AI uses its superhuman planning and persuasion capabilities to ensure that the rollout goes smoothly. Some humans continue to work against it, but they are discredited. The US government is sufficiently captured by the AI that it is very unlikely to shut it down. ", 
    position: 'above',
    verticalOffset: -25,
    horizontalOffset: -10,
    ending: 'race'
  },
  { 
    point: processedData.raceTimeline.find(p => p.rdMultiple >= 1000) || processedData.raceTimeline[0],
    label: 'Fast robot\nbuildup and\nbioweapon',
    description: "The US uses their superintelligent AI to rapidly industrialize, manufacturing robots so that the AI can operate more efficiently than with humans. Unfortunately, the AI is deceiving them. Once a sufficient number of robots have been built, the AI releases a bioweapon, killing all humans. Then, it continues the industrialization, and launches Von Neumann probes to colonize space. ",
    special: true,
    bold: true,
    position: 'right',
    verticalOffset: -90,
    horizontalOffset: 5,
    ending: 'race'
  }
];

const slowdownEvents: Event[] = [
  { 
    point: processedData.slowdownTimeline.find(p => p.rdMultiple >= 30) || processedData.mainTimeline[0],
    label: 'US centralizes compute\nand brings in\nexternal oversight',
    description: "The US combines its AGI projects in order to give OpenBrain more resources. As part of this shakeup, external researchers are brought in, assisting the alignment effort. They switch to an architecture that preserves the chain of thought, allowing them to catch misalignment as it emerges. These AIs — which are able to be monitored much more robustly — make breakthrough advances in AI alignment. They build a superintelligence which is aligned to senior OpenBrain and government officials, giving them power over the fate of humanity.",
    position: 'above',
    verticalOffset: 80,
    horizontalOffset: 20,
    ending: 'slowdown',
    anchor: 'middle'
  },
  { 
    point: processedData.slowdownTimeline.find(p => p.rdMultiple >= 100) || processedData.slowdownTimeline[0],
    label: 'OpenBrain\nCommittee\nTakeover',
    description: "The superintelligence, aligned with an oversight committee of OpenBrain leadership and government officials, gives the committee extremely good advice to further their own goals. Thankfully, the committee uses its power in a way that is largely good for the world: the AI is released to the public, spurring a period of rapid growth and prosperity. The main obstacle is that China’s AI — which is also superintelligent by now, is misaligned. But it is less capable and has less compute than the U.S. AI, and so the U.S can make a favorable deal, giving the Chinese AI some resources in the depth of space in return for its cooperation now. The rockets start launching, and a new age dawns. ",
    special: true,
    bold: true,
    position: 'right',
    verticalOffset: 30,
    horizontalOffset: -75,
    ending: 'slowdown',
    anchor: 'middle'
  }
];

// Generate date labels
const dateLabels = (() => {
  const labels = [];
  
  // Pre-2027 year labels
  labels.push({ date: new Date("2026-01-01"), label: "2026", type: "year" });
  
  // 2027 year label and monthly labels
  labels.push({ date: new Date("2027-01-01"), label: "2027", type: "year" });
  const months = ["Apr", "Jul", "Oct"];
  for (let month = 0; month < 12; month++) {
    labels.push({ 
      date: new Date(2027, (month + 1) * 3, 1),
      label: months[month],
      type: "month"
    });
  }
  
  // 2028 year label
  labels.push({ date: new Date("2028-01-01"), label: "2028", type: "year" });
  
  return labels.map(label => ({
    ...label,
    x: dateToX(label.date)
  }));
})();

const SummaryChart = () => {
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);

  // Create path string from array of points
  const createPath = (points: TimelinePoint[]) => {
    return points.map((point, i) => {
      const y = logScaleY(point.rdMultiple);
      return `${i === 0 ? 'M' : 'L'} ${point.x} ${y}`;
    }).join(' ');
  };

  // Find threshold crossings
  const findThresholdCrossings = (timeline: TimelinePoint[]): ThresholdCrossing[] => {
    const crossings: ThresholdCrossing[] = [];
    
    for (let i = 1; i < timeline.length; i++) {
      const prev = timeline[i-1];
      const curr = timeline[i];
      
      thresholds.forEach(threshold => {
        if ((prev.rdMultiple < threshold.value && curr.rdMultiple >= threshold.value) ||
            (prev.rdMultiple >= threshold.value && curr.rdMultiple < threshold.value)) {
          
          const ratio = (threshold.value - prev.rdMultiple) / (curr.rdMultiple - prev.rdMultiple);
          const x = prev.x + ratio * (curr.x - prev.x);
          
          const alreadyCrossed = crossings.some(c => 
            c.rdMultiple === threshold.value && c.timelineType === (timeline === processedData.mainTimeline ? 'main' : 'race')
          );
          
          if (!alreadyCrossed) {
            crossings.push({
              x,
              rdMultiple: threshold.value,
              label: threshold.label,
              timelineType: timeline === processedData.mainTimeline ? 'main' : 'race'
            });
          }
        }
      });
    }
    
    return crossings;
  };

  const mainCrossings = findThresholdCrossings(processedData.mainTimeline);
  const raceCrossings = findThresholdCrossings(processedData.raceTimeline);
  const allCrossings = [...mainCrossings, ...raceCrossings];

  const getFirstCrossingX = (thresholdValue: number) => {
    const matchingCrossings = allCrossings.filter(c => c.rdMultiple === thresholdValue);
    if (matchingCrossings.length === 0) return null;
    return Math.min(...matchingCrossings.map(c => c.x));
  };

  const isThresholdCrossed = (threshold: { value: number }) => {
    return processedData.mainTimeline.some(point => point.rdMultiple >= threshold.value) || 
           processedData.raceTimeline.some(point => point.rdMultiple >= threshold.value);
  };

  return (
    <div className="md:h-[calc(100vh-120px)] w-full md:mt-[-50px] relative overflow-hidden summary-chart">
      <svg 
        // width="100%" 
        height="100%"
        style={{maxWidth: '100%'}}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Threshold lines with labels */}
        {thresholds.map((threshold, i) => {
          const y = logScaleY(threshold.value);
          if (!isThresholdCrossed(threshold)) return null;
          
          const firstCrossingX = getFirstCrossingX(threshold.value);
          if (firstCrossingX === null) return null;

          const lineCount = threshold.label.split('\n').length;
          const lineHeight = 11;
          const totalHeight = lineCount * lineHeight;
          const initialOffset = -(totalHeight / 2) + (lineHeight / 2);

          return (
            <g key={`threshold-${i}`}>
              <line 
                x1={PADDING.left} 
                y1={y} 
                x2={firstCrossingX} 
                y2={y} 
                stroke="#ddd" 
                strokeWidth="0.8" 
                strokeDasharray="5,5"
              />
              
              <text 
                x={PADDING.left - 10} 
                y={y + 2} 
                textAnchor="start" 
                className="text-[10px] font-mono"
              >
                <tspan  
                  x={PADDING.left + 1} 
                  dy={-5}
                >
                  {threshold.label.split('\n').join(' ')}
                </tspan>
              </text>
              <text 
                x={PADDING.left + 1} 
                y={y -3} 
                textAnchor="start" 
                fill="#aaaaaa"
                className="text-[8px] font-mono"
              >
                
              </text>
              {!(threshold.value === 1.3) && (<text 
                x={PADDING.left + 1} 
                y={y +9} 
                textAnchor="start" 
                fill="#aaaaaa"
                className="text-[8px] font-mono"
              >
                {threshold.value}x AI R&D Multiplier
              </text>)}
            </g>
          );
        })}

        {/* Date labels */}
        <g className="text-gray-500">
          {dateLabels.map((tick, i) => {
            let labelClass = "text-[9px] font-mono";
            let yOffset = HEIGHT - PADDING.bottom + 15;
            
            if (tick.type === "year") {
              labelClass = "text-[11px] font-mono";
              yOffset = HEIGHT - PADDING.bottom + 20;
            } else if (tick.type === "month") {
              labelClass = "text-[8px] font-mono";
              yOffset = HEIGHT - PADDING.bottom + 18;
            }
            
            return (
              <text 
                key={`date-${i}`}
                x={tick.x} 
                y={yOffset} 
                textAnchor="end"
                className={labelClass}
              >
                {tick.label}
              </text>
            );
          })}
        </g>

        {/* Timeline paths */}
        <path 
          d={createPath(processedData.mainTimeline)}
          stroke={mainColor}
          strokeWidth="2"
          fill="none"
        />

        <path 
          d={createPath(processedData.raceTimeline)}
          stroke={raceColor}
          strokeWidth="2"
          fill="none"
        />

        <path 
          d={createPath(processedData.slowdownTimeline)}
          stroke={slowdownColor}
          strokeWidth="2"
          fill="none"
        />

        {/* Event points and labels */}
        {[...events, ...raceEvents, ...slowdownEvents].map((event, i) => {
          const y = logScaleY(event.point.rdMultiple);
          
          // Calculate label position
          const baseX = event.position === 'right' ? event.point.x + 10 : event.point.x;
          const labelX = baseX + (event.horizontalOffset || 0);
          const labelY = y + (event.verticalOffset || 0);
          const anchor = event.anchor || "end";
          
          return (
            <g 
              key={`event-${i}`}
              onMouseEnter={() => setHoveredEvent(event.label)}
              onMouseLeave={() => setHoveredEvent(null)}
            >
              {!event.special && (
                <circle 
                  cx={event.point.x} 
                  cy={y} 
                  r="4"
                  fill={event.point === processedData.branchPoint ? mainColor : 
                        event.ending === 'race' ? raceColor : 
                        event.ending === 'slowdown' ? slowdownColor : 
                        mainColor}
                />
              )}
              <text
                x={labelX}
                y={labelY}
                stroke="var(--vivid-background)"
                strokeWidth="2.6px"
                textAnchor={anchor}
                paintOrder="stroke fill"
                className={`text-[10px] font-mono ${event.bold ? 'font-bold' : ''}`}
                fill={event.point === processedData.branchPoint ? mainColor : 
                      event.ending === 'race' ? raceColor : 
                      event.ending === 'slowdown' ? slowdownColor : 
                      mainColor}
              >
                {event.label.split('\n').map((line, j) => (
                  <tspan 
                    key={j} 
                    x={labelX} 
                    dy={j === 0 ? "0" : "12"}
                  >
                    {line}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}

        {/* Tooltip */}
        {hoveredEvent && (
          <foreignObject 
            className="pointer-events-none"
            x={PADDING.left} 
            y={PADDING.top} 
            width={300} 
            height={300}
          >
            <div className="bg-[var(--vivid-background)] font-mono p-3 border border-black rounded text-[8px] [&>p]:text-[10px]">
              <p className="font-bold mb-2 mt-0">{hoveredEvent.split('\n').join(' ')}</p>
              <p className="mb-0 text-[10px]">
                {[...events, ...raceEvents, ...slowdownEvents].find(e => e.label === hoveredEvent)?.description || 
                 "No description available for this event."}
              </p>
            </div>
          </foreignObject>
        )}
      </svg>
    </div>
  );
};

export default SummaryChart;
